// @ts-nocheck — aspirational data shapes / API mismatch; tracked under Task #75 (type-safety debt)
import { ReactNode, useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { mololinkPath } from "@/lib/mololink-path";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Home, User, LogOut, Menu, ChevronLeft, ChevronRight, 
  Package, Bell, Search, Wallet, ExternalLink,
  ShoppingBag, Gavel, FileText, Users, Building2,
  MessageCircle, Compass, Globe, BarChart3, DollarSign,
  Truck, Bot, Settings, Briefcase, Star
} from "lucide-react";
import { PageTransition } from "@/components/ui/page-transition";
import Navigation from "@/components/layout/Navigation";

interface MololinkLayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
  requireAuth?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    title: "Discover",
    items: [
      { path: "/home", label: "Feed", icon: Home, requireAuth: true },
      { path: "/marketplace", label: "Marketplace", icon: ShoppingBag },
      { path: "/providers", label: "Providers", icon: Users },
      { path: "/companies", label: "Companies", icon: Building2 },
      { path: "/explorer", label: "Explorer", icon: Compass, requireAuth: true },
      { path: "/search", label: "Search", icon: Search },
    ],
  },
  {
    title: "Trade",
    items: [
      { path: "/auctions", label: "Auctions", icon: Gavel, requireAuth: true },
      { path: "/requests", label: "Requests", icon: FileText, requireAuth: true },
    ],
  },
  {
    title: "Connect",
    items: [
      { path: "/messaging", label: "Messaging", icon: MessageCircle, requireAuth: true },
      { path: "/notifications", label: "Notifications", icon: Bell, requireAuth: true },
    ],
  },
  {
    title: "Info",
    items: [
      { path: "/solutions", label: "Solutions", icon: Globe },
      { path: "/resources", label: "Resources", icon: Briefcase },
      { path: "/pricing", label: "Pricing", icon: DollarSign },
    ],
  },
];

const bottomItems: NavItem[] = [
  { path: "/profile", label: "My Profile", icon: User, requireAuth: true },
];

export default function MololinkLayout({ children }: MololinkLayoutProps) {
  const [location, navigate] = useLocation();
  const { user, logoutMutation } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      window.location.href = "https://auth.molochain.com/login";
    } catch (error) {}
  };

  const NavLink = ({ item, collapsed = false }: { item: NavItem; collapsed?: boolean }) => {
    const fullPath = mololinkPath(item.path);
    const isActive = location === fullPath || location.startsWith(fullPath + "/");
    const Icon = item.icon;

    if (item.requireAuth && !user) return null;

    return (
      <Link href={fullPath}>
        <Button
          variant={isActive ? "secondary" : "ghost"}
          className={cn(
            "w-full justify-start gap-3 h-9 text-sm font-medium transition-colors",
            isActive 
              ? "bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800" 
              : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
            collapsed && "justify-center px-2"
          )}
          onClick={() => setMobileOpen(false)}
        >
          <Icon className={cn("h-4 w-4 shrink-0", isActive && "text-purple-600 dark:text-purple-400")} />
          {!collapsed && <span className="truncate">{item.label}</span>}
          {!collapsed && item.badge && item.badge > 0 && (
            <Badge variant="destructive" className="ml-auto h-5 min-w-5 text-[10px] px-1">
              {item.badge}
            </Badge>
          )}
        </Button>
      </Link>
    );
  };

  const SidebarContent = ({ collapsed = false }: { collapsed?: boolean }) => (
    <div className="flex flex-col h-full">
      <div className={cn("flex items-center gap-2 px-3 py-4 border-b", collapsed && "justify-center px-2")}>
        <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg shrink-0">
          <Package className="h-5 w-5 text-white" />
        </div>
        {!collapsed && (
          <div>
            <span className="text-base font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
              MOLOLINK
            </span>
            <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0 border-amber-300 bg-amber-50 text-amber-700">
              Beta
            </Badge>
          </div>
        )}
      </div>

      <ScrollArea className="flex-1 px-2 py-3">
        <div className="space-y-4">
          {navGroups.map((group) => {
            const visibleItems = group.items.filter(item => !item.requireAuth || user);
            if (visibleItems.length === 0) return null;
            
            return (
              <div key={group.title}>
                {!collapsed && (
                  <p className="px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                    {group.title}
                  </p>
                )}
                {collapsed && <Separator className="my-2" />}
                <div className="space-y-0.5">
                  {visibleItems.map((item) => (
                    <NavLink key={item.path} item={item} collapsed={collapsed} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      <div className="border-t px-2 py-3 space-y-1">
        {bottomItems.map((item) => (
          <NavLink key={item.path} item={item} collapsed={collapsed} />
        ))}
        
        <a href="https://molochain.com/dashboard" className="block">
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start gap-3 h-9 text-sm text-muted-foreground hover:text-foreground",
              collapsed && "justify-center px-2"
            )}
          >
            <Home className="h-4 w-4 shrink-0" />
            {!collapsed && (
              <>
                <span className="truncate">Main Portal</span>
                <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground/50" />
              </>
            )}
          </Button>
        </a>

        {user && (
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start gap-3 h-9 text-sm text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20",
              collapsed && "justify-center px-2"
            )}
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!collapsed && <span>Log out</span>}
          </Button>
        )}
      </div>
    </div>
  );

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <div className="flex-1 pt-16 overflow-x-hidden">
          <main>
            <PageTransition>{children}</PageTransition>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <div className="flex flex-1 pt-16">
        <aside className={cn(
          "hidden lg:flex flex-col border-r bg-card/50 transition-all duration-300 sticky top-16 h-[calc(100vh-4rem)]",
          sidebarCollapsed ? "w-16" : "w-56"
        )}>
          <SidebarContent collapsed={sidebarCollapsed} />
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute -right-3 top-20 z-10 flex h-6 w-6 items-center justify-center rounded-full border bg-background shadow-sm hover:bg-muted transition-colors"
          >
            {sidebarCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
          </button>
        </aside>

        <div className="flex-1 flex flex-col min-h-[calc(100vh-4rem)] min-w-0 overflow-x-hidden">
          <header className="sticky top-0 z-40 flex h-12 items-center gap-3 border-b bg-card border-border shadow-sm px-4 lg:px-6">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden h-8 w-8">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <SidebarContent />
              </SheetContent>
            </Sheet>

            <div className="flex-1 flex items-center">
              <div className="lg:hidden flex items-center gap-2">
                <div className="w-6 h-6 bg-gradient-to-r from-purple-600 to-indigo-600 rounded flex items-center justify-center">
                  <Package className="h-3.5 w-3.5 text-white" />
                </div>
                <span className="text-sm font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  MOLOLINK
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(mololinkPath("/search"))}>
                <Search className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="relative h-8 w-8" onClick={() => navigate(mololinkPath("/notifications"))}>
                <Bell className="h-4 w-4" />
                <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                      {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user?.username} />}
                      <AvatarFallback className="bg-gradient-to-br from-purple-500/20 to-indigo-500/10 text-xs">
                        {user?.firstName?.charAt(0) || user?.username?.charAt(0)?.toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.firstName || user?.username}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(mololinkPath("/profile"))}>
                    <User className="mr-2 h-4 w-4" />Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(mololinkPath("/home"))}>
                    <Home className="mr-2 h-4 w-4" />Feed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(mololinkPath("/marketplace"))}>
                    <ShoppingBag className="mr-2 h-4 w-4" />Marketplace
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <a href="https://molochain.com/dashboard" className="flex items-center">
                      <Home className="mr-2 h-4 w-4" />Main Portal
                      <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main className="flex-1 overflow-x-hidden overflow-y-auto">
            <PageTransition>{children}</PageTransition>
          </main>
        </div>
      </div>
    </div>
  );
}
